<template>
  <ChartBox
    class="fan-base"
    :title="titleTableGrowth"
    :description="descriptionTableGrowth"
  >
    <SkeletonTable
      v-if="isLoading"
      :count-table-row="countTableRow"
    />

    <ErrorDefault
      :show="hasError"
      @onClickButton="getFanbaseGrowthTable()"
    />

    <template
      #primary-action
    >
      <DownloadMenu
        v-if="!hasError && !isLoading"
        track-name-on-open="BM ExportFanbaseGrowth Table Open"
        :download-list="[
          { label: 'CSV', type: 'csv' },
          { label: 'XLS', type: 'xls' },
        ]"
        @selectDownload="handleDownload"
      />
    </template>

    <div
      v-if="!hasError && !isLoading"
      v-observe.once="onObserver"
    >
      <v-data-table
        :headers="headers"
        :items="data"
        :items-per-page="10"
        :hide-default-footer="true"
        :sort-by="'total_followers'"
        :sort-desc="true"
        @update:sort-by="trackSortBy"
      >
        <template
          v-for="(header, headerKey) in headers"
          v-slot:[`header.${header.value}`]
          text-right
        >
          <TableHeaderItem
            :key="headerKey"
            :text="header.text"
            :tooltip-text="header.tooltip"
            @show-tooltip="trackTooltipUsage"
          />
        </template>

        <template
          v-slot:body="{ items }"
        >
          <tbody>
            <tr
              v-for="(item, itemKey) in items"
              :key="itemKey"
            >
              <td>{{ item.name }}</td>
              <td class="text-right">
                {{ item.totalFollowersFormatted }}
              </td>
              <td class="text-right">
                {{ item.averageIncreaseFormatted }}
              </td>
              <td class="text-right">
                {{ item.maxIncreaseDateFormatted }}
              </td>
              <td class="text-right">
                {{ item.maxIncreaseFormatted }}
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
    </div>
  </ChartBox>
</template>

<script>
import { SkeletonTable } from '@/components/content/tables/SkeletonTable';
import { parseISO, format } from 'date-fns';

import { ErrorDefault, DownloadMenu, ChartBox } from '@/components/base';
import { TableHeaderItem } from '@/components/content/TableHeaderItem';

import { dispatchMixpanelTrack, numberFormatToPtBr } from '@/utils';

import { ExportToCsv } from 'export-to-csv';
import { json2excel } from 'js2excel';

import { generateFileName } from '@/utils/generate-file-name.js';

export default {
  name: 'FanbaseGrowthTable',
  components: {
    SkeletonTable,
    ChartBox,
    ErrorDefault,
    TableHeaderItem,
    DownloadMenu,
  },
  data() {
    return {
      observer: null,
    };
  },
  computed: {
    titleTableGrowth() {
      return !this.isLoading ? 'Variação do total da base de fãs/inscritos' : '';
    },
    descriptionTableGrowth() {
      return !this.isLoading ? 'Desempenho total da variação no período selecionado.' : '';
    },
    isLoading() {
      return this.$store.state.charts.fanbaseGrowthTable.isLoading;
    },
    hasError() {
      return this.$store.state.charts.fanbaseGrowthTable.hasError;
    },
    headers() {
      const headersList = [
        {
          text: 'Grupos',
          align: 'left',
          sortable: true,
          value: 'name',
        },
        {
          text: 'Total de fãs',
          align: 'right',
          tooltip: 'Total de fãs no período selecionado.',
          value: 'total_followers',
        },
        {
          text: 'Média de crescimento diário',
          align: 'right',
          tooltip: 'Ganho de fãs por dia dividido pela quantidade de dias do período selecionado.',
          value: 'average_increase',
        },
        {
          text: 'Dia de maior ganho',
          align: 'right',
          tooltip: 'Dia de maior ganho de fãs no período selecionado.',
          value: 'max_increase_date',
        },
        {
          text: 'Maior ganho',
          align: 'right',
          tooltip: 'Quantidade de fãs conquistados no dia de maior ganho no período selecionado.',
          value: 'max_increase',
        },
      ];

      const headerConfig = {
        TODAS: {
          total_followers: {
            text: 'Total de fãs/inscritos',
            tooltip: 'Total de fãs/inscritos no período selecionado.',
          },
          average_increase: {
            tooltip: 'Ganho de fãs/inscritos por dia dividido pela quantidade de dias do período selecionado.',
          },
          max_increase_date: {
            tooltip: 'Dia de maior ganho de fãs/inscritos no período selecionado.',
          },
          max_increase: {
            tooltip: 'Quantidade de fãs/inscritos conquistados no dia de maior ganho no período selecionado.',
          },
        },
        YOUTUBE: {
          total_followers: {
            text: 'Total de inscritos',
            tooltip: 'Total de inscritos no período selecionado.',
          },
          average_increase: {
            tooltip: 'Ganho de inscritos por dia dividido pela quantidade de dias do período selecionado.',
          },
          max_increase_date: {
            tooltip: 'Dia de maior ganho de inscritos no período selecionado.',
          },
          max_increase: {
            tooltip: 'Quantidade de inscritos conquistados no dia de maior ganho no período selecionado.',
          },
        },
      };

      const channelUpperCase = this.$store.state.ui.channel.toUpperCase();

      const changeHeaderProps = (header, props) => ({
        ...header,
        ...props,
      });

      const newHeader = () => {
        return headersList.map(h => {
          const totalFollowers = 'total_followers';
          const averageIncrease = 'average_increase';
          const maxIncreaseDate = 'max_increase_date';
          const maxIncrease = 'max_increase';

          const headerTextDynamic = {
            total_followers: changeHeaderProps(h, headerConfig[channelUpperCase][totalFollowers]),
            average_increase: changeHeaderProps(h, headerConfig[channelUpperCase][averageIncrease]),
            max_increase_date: changeHeaderProps(h, headerConfig[channelUpperCase][maxIncreaseDate]),
            max_increase: changeHeaderProps(h, headerConfig[channelUpperCase][maxIncrease]),
          };

          if (h.value in headerTextDynamic) return headerTextDynamic[h.value];

          return h;
        });
      };

      return channelUpperCase in headerConfig ? newHeader() : headersList;
    },
    data() {
      if (this.$store.state.charts.fanbaseGrowthTable.data.length > 0) {
        return this.formatFanbaseEvolutionData(this.$store.state.charts.fanbaseGrowthTable.data);
      }

      return this.$store.state.charts.fanbaseGrowthTable.data;
    },
    countTableRow() {
      return this.$store.state.ui.groupList.length;
    },
  },
  async mounted() {
    const isNoneRequestRunning = !this.isLoading;

    if (isNoneRequestRunning) {
      await this.getFanbaseGrowthTable();
    }
  },
  methods: {
    async getFanbaseGrowthTable() {
      await this.$store.dispatch('charts/fetchFanbaseEvolutionTable');
    },
    formatFanbaseEvolutionData(data) {
      return data.map(item => ({
        ...item,
        totalFollowersFormatted: this.formatToLocaleStringPtBr(item.total_followers),
        averageIncreaseFormatted: numberFormatToPtBr(item.average_increase),
        maxIncreaseDateFormatted: this.formatedDate(item.max_increase_date),
        maxIncreaseFormatted: this.formatToLocaleStringPtBr(item.max_increase),
      }));
    },
    formatedDate(date) {
      return format(parseISO(date), 'dd/LL/yyyy');
    },
    formatToLocaleStringPtBr(value) {
      return (value).toLocaleString('pt-BR');
    },
    trackTooltipUsage(text) {
      dispatchMixpanelTrack('BM FanbaseGrowth TooltipTable View', ['network', 'tab'], {
        answer: text,
      });
    },
    trackSortBy(sortBy) {
      if (sortBy) {
        dispatchMixpanelTrack('BM FanbaseGrowth ReorderTable Click', ['network', 'tab'], {
          answer: this.headers.find(header => sortBy.includes(header.value))?.text,
        });
      }
    },
    onObserver() {
      const eventName = 'BM FanbaseGrowth Table View';

      dispatchMixpanelTrack(eventName, ['network']);
    },
    dispatchTrackOnDownload(type) {
      const eventName = 'BM ExportFanbaseGrowth Table Click';

      dispatchMixpanelTrack(eventName, ['network', 'tab', 'groups'], {
        answer: type,
      });
    },
    handleDownload({ typeSelected }) {
      const exportTypes = {
        csv: 'exportTableCSV',
        xls: 'exportTableXLS',
      };

      if (!(typeSelected in exportTypes)) return;

      const functionName = exportTypes[typeSelected];
      this[functionName]();

      this.dispatchTrackOnDownload(typeSelected);
    },
    dataToExport() {
      return this.data.map((p) => {
        return Object.entries(p).map(([key]) => key).reduce((acc, item) => {
          const keyName = this.headers.find(h => h.value === item)?.text;

          if (keyName) {
            const value = p[item];

            acc[keyName] = value;

            const valuesFormatted = {
              total_followers: this.formatToLocaleStringPtBr,
              average_increase: numberFormatToPtBr,
              max_increase_date: this.formatedDate,
              max_increase: this.formatToLocaleStringPtBr,
            };

            if (item in valuesFormatted) {
              acc[keyName] = valuesFormatted[item](value);
            }
          }

          return acc;
        }, {});
      });
    },
    exportTableCSV() {
      const filename = generateFileName('variação_base_fas');

      const options = {
        fieldSeparator: ';',
        showLabels: true,
        useBom: true,
        useKeysAsHeaders: true,
        filename,
      };

      new ExportToCsv(options).generateCsv(this.dataToExport());
    },
    exportTableXLS() {
      const filename = generateFileName('variação_base_fas');

      json2excel({
        data: this.dataToExport(),
        name: filename,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.fan-base {
  margin-top: 32px;
}

::v-deep {
  .v-data-table {
    table tbody tr td {
      font-size: var(--ds-font-size-xs);
    }
  }
}
</style>
